import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import ParticipantInfo from './ParticipantInfo';
import ClinicalMeasurements from './ClinicalMeasurements';
import GeneralCheckup from './GeneralCheckup';
import CardiacAuscultation from './CardiacAuscultation';
import ECGForm from './ECGForm';
import EchoCardiogram from './EchoCardiogram';
import axios from 'axios';
import ConsentUpload from './ConsentUpload';
import UploadFiles from './UploadFiles';

const PageContainer = styled.div`
  width: 100%;
  background-color: #F8FCFF;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 30px;
`;

const FormContainer = styled.div`
  width: 100%;
  background-color: #F8FCFF;
`;

const DataCollection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 24px 0;
  gap: 16px;
  width: 100%;
  height: 70px;
`;

const ParticipantIdContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 8px;
`;

const ParticipantIdLabel = styled.span`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  color: #2C2C2D;
`;

const ParticipantIdValue = styled.span`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #6D6D71;
`;

const TabsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: 100%;
  height: 40px;
  background: #F8FCFF;
  border-bottom: 1px solid #E2E2E3;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 0 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 16px;
  color: ${props => props.active ? '#2575BD' : '#6D6D71'};
  cursor: pointer;
  height: 100%;
  border-bottom: ${props => props.active ? '2px solid #2575BD' : 'none'};

  &:hover {
    color: #2575BD;
  }
`;

const TabContent = styled.div`
  padding: 24px 0;
`;

const BackIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const MedicalForm = () => {
  const { participantId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('participantInfo');
  const [formData, setFormData] = useState({
    participantInfo: null,
    clinicalMeasurements: null,
    generalCheckup: null,
    cardiacAuscultation: null,
    ecgForm: {
      basic_measurements: {},
      wave_amplitudes: {},
      criteria: {
        left_ventricular_hypertrophy: {}
      },
      rhythm_and_conduction: {
        arrhythmias: {},
        bundle_branch_block: {},
        other_findings: {}
      }
    },
    echocardiogram: {
      ventricular_measurements: {},
      additional_measurements: {},
      mitral_valve: {},
      tricuspid_valve: {},
      aortic_valve: {},
      pulmonary_valve: {},
    }
  });

  useEffect(() => {
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const tabs = [
    { id: 'participantInfo', label: 'Participant Info', component: ParticipantInfo },
    { id: 'clinicalMeasurements', label: 'Clinical Measurements', component: ClinicalMeasurements },
    { id: 'generalCheckup', label: 'General Checkup', component: GeneralCheckup },
    { id: 'cardiacAuscultation', label: 'Cardiac Auscultation', component: CardiacAuscultation },
    { id: 'ecgForm', label: 'ECG', component: ECGForm },
    { id: 'echocardiogram', label: 'Echocardiogram', component: EchoCardiogram },
    { id: 'fileUpload', label: 'File Upload', component: UploadFiles },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleFormDataChange = (tabId, data) => {
    setFormData(prevData => ({
      ...prevData,
      [tabId]: data
    }));
  };

  const handleSaveSuccess = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      handleTabChange(tabs[currentIndex + 1].id);
    }
  };

  const handleBackClick = () => {
    navigate('/listing');
  };

  const ActiveComponent = tabs.find(tab => tab.id === activeTab).component;

  return (
    <PageContainer>
      <ContentContainer>
        <FormContainer>
          <DataCollection>
            <BackIcon 
              src="/back_icon.png" 
              alt="Back Icon" 
              onClick={handleBackClick}
            />
            <ParticipantIdContainer>
              <ParticipantIdLabel>Participant ID:</ParticipantIdLabel>
              <ParticipantIdValue>{participantId}</ParticipantIdValue>
            </ParticipantIdContainer>
          </DataCollection>
          <TabsContainer>
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabsContainer>
          <TabContent>
            {activeTab === 'fileUpload' ? (
              <UploadFiles 
                participantId={participantId}
                onSaveSuccess={handleSaveSuccess}
              />
            ) : (
              <ActiveComponent 
                participantId={participantId}
                formData={formData[activeTab]}
                onFormDataChange={(data) => handleFormDataChange(activeTab, data)}
                onSaveSuccess={handleSaveSuccess}
              />
            )}
          </TabContent>
        </FormContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default MedicalForm;
